import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createNewAdmin } from "@/config/mutation/super-admin";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs";
import { Form } from "@/components/ui/form/form";
import { Button } from "@/components/ui/button/button";
import { NewAdminOverview } from "./comps/new-admin-overview";

const formSchema = z.object({
  // ? TAB Overview
  name: z
    .string({
      required_error: "Employee name required!",
    })
    .min(1, {
      message: "Employee name is required!",
    }),
  email: z
    .string({
      required_error: "Email is required!",
    })
    .email({
      message: "Invalid Email",
    }),
  password: z
    .string({
      required_error: "Password required!",
    })
    .min(1, {
      message: "Password is required!",
    }),
  phone: z.string().length(10, {
    message: "Phone number should of length 10 digit",
  }),
});

export type FormValues = z.infer<typeof formSchema>;

export const NewAdminForm = () => {
  const navigate = useNavigate();
  const adminKey = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // ? TAB Overview
      name: "",
      email: "",
      password: "",
      phone: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createNewAdmin(adminKey),
  });

  function onSubmit(values: FormValues) {
    // console.log(values);

    mutate(
      {
        name: values.name,
        email: values.email,
        password: values.password,
        phone: values.phone,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["sa-admins"],
          });

          toast.success("New Admin Created!");

          navigate("/superadmin/admins", {
            replace: true,
          });
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING NEW ADMIN", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  return (
    <div className="p-5">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-medium">New Admin</h2>

            <Button type="submit" disabled={isPending} size="sm">
              Continue
            </Button>
          </div>

          <div className="mt-5">
            <Tabs defaultValue="overview" className="mr-auto w-full">
              <div className="flex items-center justify-between pb-3 sticky top-[56px] bg-card z-30">
                <TabsList className="w-full justify-start rounded-none border-b border-b-gray-500 bg-transparent p-0">
                  <TabsTrigger
                    value="overview"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Overview
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="relative rounded-md">
                <NewAdminOverview isPending={isPending} />
              </TabsContent>
            </Tabs>
          </div>
        </form>
      </Form>
    </div>
  );
};
